/**
 * ==========================================================================
 * Layout: Header — Mobile Menu Toggle
 * ==========================================================================
 *
 * Handles hamburger menu open/close, body scroll lock, focus trapping,
 * and Escape key dismissal.
 *
 * HTML hooks:
 *   [data-menu-toggle]  — the hamburger/close button
 *   [data-mobile-menu]  — the mobile menu overlay
 *
 * ==========================================================================
 */

document.addEventListener('DOMContentLoaded', () => {
    const root = findRoot();

    // Pages may load this script more than once (every shadow root pulling in <shell-script />), and each copy
    // resolves to the same root — binding twice would make a single click run the toggle twice.
    if (root.cmcaHeaderInitialised) return;
    root.cmcaHeaderInitialised = true;

    let bodyOverflow = '';

    // Mobile nav dropdown toggles
    root.querySelectorAll('[data-dropdown-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            // Allow clicks on the <a> inside to navigate normally
            if (e.target.closest('a')) return;

            const dropdown = btn.nextElementSibling;
            if (!dropdown) return;

            const isOpen = btn.getAttribute('aria-expanded') === 'true';

            // Collapse any other open dropdown (accordion behaviour)
            if (!isOpen) {
                root.querySelectorAll('[data-dropdown-toggle][aria-expanded="true"]').forEach((other) => {
                    other.setAttribute('aria-expanded', 'false');
                    if (other.nextElementSibling) other.nextElementSibling.hidden = true;
                });
            }

            if (isOpen) {
                btn.setAttribute('aria-expanded', 'false');
                dropdown.hidden = true;
            } else {
                btn.setAttribute('aria-expanded', 'true');
                dropdown.hidden = false;
            }
        });
    });

    const toggle = root.querySelector('[data-menu-toggle]');
    const menu = root.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;

    let pendingHide = null;

    // A close that never transitions (already faded out) would otherwise leave the handler attached and let it
    // fire on the next fade-IN, hiding a menu the user just opened.
    function cancelPendingHide() {
        if (!pendingHide) return;
        menu.removeEventListener('transitionend', pendingHide);
        pendingHide = null;
    }

    function openMenu() {
        cancelPendingHide();
        menu.hidden = false;
        // Allow a frame for the hidden removal to take effect before animating
        requestAnimationFrame(() => {
            menu.classList.add('is-visible');
        });
        toggle.setAttribute('aria-expanded', 'true');
        toggle.setAttribute('aria-label', 'Close menu');

        bodyOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';

        // Auto-expand the active portal's dropdown
        const navList = menu.querySelector('[data-active-portal]');
        if (navList) {
            const activePortal = navList.dataset.activePortal;
            if (activePortal) {
                const activeBtn = navList.querySelector(`[data-portal="${activePortal}"]`);
                if (activeBtn) {
                    const dropdown = activeBtn.nextElementSibling;
                    if (dropdown) {
                        activeBtn.setAttribute('aria-expanded', 'true');
                        dropdown.hidden = false;
                    }
                }
            }
        }

        // Focus first link in the menu
        requestAnimationFrame(() => {
            const firstFocusable = menu.querySelector('a, button');
            if (firstFocusable) firstFocusable.focus();
        });
    }

    function closeMenu() {
        menu.classList.remove('is-visible');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open menu');
        document.documentElement.style.overflow = bodyOverflow;

        // Hide after transition completes — transitionend bubbles, so ignore descendant transitions
        cancelPendingHide();
        pendingHide = (e) => {
            if (e.target !== menu) return;
            menu.hidden = true;
            cancelPendingHide();
        };
        menu.addEventListener('transitionend', pendingHide);

        toggle.focus();
    }

    // Toggle on click
    toggle.addEventListener('click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        isOpen ? closeMenu() : openMenu();
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
            closeMenu();
        }
    });

    // Focus trapping within the mobile menu
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;

        const focusable = menu.querySelectorAll('a[href], button:not([disabled])');
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && root.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && root.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    });

    let resizeTimer;

    // Closees menu if page is resized
    window.addEventListener('resize', () => {
        // Debounce to avoid firing on every pixel change
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            if (isOpen) {
                closeMenu();
            }
        }, 100);
    });
});

function findRoot() {
    // Try shadow DOM first
    const host = document.querySelector('shadow-header');
    if (host?.shadowRoot?.querySelector('[data-menu-toggle]')) {
        return host.shadowRoot;
    }
    // Fall back to light DOM
    return document;
}

